import { noop } from 'lodash';
import { SignatureSingleActionProps } from 'components/tables/data/signature/types';
import { Menu, Tooltip } from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import { useSignRevisionModal } from 'components/modals/sign-revision-modal/SignRevisionModalProvider';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { useNavigate } from 'react-router-dom';
import openBrainItUrl from 'utils/open-brain-it-url';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Sign again action for signatures.
 */
export default function SignAgainAction({ signature, onStatusChange = noop, kind }: SignatureSingleActionProps) {
  const { signRevisionModal } = useSignRevisionModal();
  const { getAction } = useApi();
  const navigate = useNavigate();

  /**
   * Signs the revisions by calling the API to initiate the signing process.
   */
  const signRevisions = async ({ deadline }: { deadline: string }) => {
    const revisionSignMultipleDocsAction = getAction('RevisionSignMultipleDocs');
    const revisionIds = signature.revisions.map((rev) => rev.revisionId);

    try {
      const { redirectUrl } = await revisionSignMultipleDocsAction({
        payload: {
          revisionIds,
          documentValidUntil: deadline,
        },
      });

      openBrainItUrl(redirectUrl, navigate);
    } catch (error: any) {
      panic(error);
    }
  };

  /**
   * Confirms the redirect to the third party to sign the revisions.
   */
  const confirmSignRevision = () => {
    const revisionIds = signature.revisions.map((rev) => rev.revisionId);
    const isRevisionKind = signature.revisions[0]?.kind === 'revision';

    signRevisionModal({
      revisionIds,
      message:
        revisionIds.length > 1
          ? isRevisionKind
            ? 'Revízne správy budú opätovne odoslané do systému BrainIT. Pre ich podpísanie budete presmerovaný do ich webového portálu.'
            : 'Formuláre budú opätovne odoslané do systému BrainIT. Pre ich podpísanie budete presmerovaný do ich webového portálu.'
          : isRevisionKind
          ? 'Revízna správa bude opätovne odoslaná do systému BrainIT. Pre jej podpísanie budete presmerovaný do ich webového portálu.'
          : 'Formulár bude opätovne odoslaný do systému BrainIT. Pre jeho podpísanie budete presmerovaný do ich webového portálu.',
      onSign: signRevisions,
    });
  };

  if (kind === 'action-icon') {
    return (
      <Tooltip label="Znovu podpísať" events={{ hover: true, focus: true, touch: true }}>
        <ActionIcon variant="secondary" size="md" onClick={confirmSignRevision} disabled={!signature.kepSignUrl}>
          <IconSignature stroke={1.5} size={24} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (kind === 'button') {
    return (
      <Button
        size="md"
        onClick={confirmSignRevision}
        leftIcon={<IconSignature stroke={1.5} size={24} />}
        variant="secondary"
        disabled={!signature.kepSignUrl}
      >
        Znovu podpísať
      </Button>
    );
  }

  return (
    <Menu.Item
      color="gray.8"
      onClick={confirmSignRevision}
      icon={<IconSignature stroke={1.5} size={24} />}
      disabled={!signature.kepSignUrl}
    >
      Znovu podpísať
    </Menu.Item>
  );
}
